import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const COOKIE_CONSENT_STORAGE_KEY = 'stringpak_cookie_consent';

export interface CookiePreferences {
  accepted: boolean;
  timestamp: number;
  targetedAdvertising: boolean;
  personalization: boolean;
  analytics: boolean;
}

const defaultPreferences: CookiePreferences = {
  accepted: false,
  timestamp: 0,
  targetedAdvertising: false,
  personalization: false,
  analytics: false,
};

function loadStored(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    return parsed && typeof parsed.accepted === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
}

function saveStored(prefs: CookiePreferences): void {
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export interface CookieConsentContextValue {
  preferences: CookiePreferences | null;
  hasAccepted: boolean;
  showModal: boolean;
  openCookieSettings: () => void;
  closeCookieModal: () => void;
  updatePreferences: (prefs: CookiePreferences) => void;
  allowAnalytics: boolean;
  allowPersonalization: boolean;
  allowTargetedAdvertising: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(() => loadStored());
  const [showModal, setShowModal] = useState(false);

  const hasAccepted = preferences?.accepted ?? false;

  useEffect(() => {
    if (!hasAccepted) setShowModal(true);
  }, [hasAccepted]);

  const updatePreferences = useCallback((prefs: CookiePreferences) => {
    const next = { ...prefs, timestamp: Date.now() };
    saveStored(next);
    setPreferences(next);
    setShowModal(false);
  }, []);

  const openCookieSettings = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeCookieModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const allowAnalytics = preferences?.analytics ?? false;
  const allowPersonalization = preferences?.personalization ?? false;
  const allowTargetedAdvertising = preferences?.targetedAdvertising ?? false;

  const value: CookieConsentContextValue = {
    preferences,
    hasAccepted,
    showModal,
    openCookieSettings,
    closeCookieModal,
    updatePreferences,
    allowAnalytics,
    allowPersonalization,
    allowTargetedAdvertising,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    return {
      preferences: null,
      hasAccepted: false,
      showModal: false,
      openCookieSettings: () => {},
      closeCookieModal: () => {},
      updatePreferences: () => {},
      allowAnalytics: false,
      allowPersonalization: false,
      allowTargetedAdvertising: false,
    };
  }
  return ctx;
}
